import { useEffect, useMemo, useState } from 'react';
import { FinanceiroService } from './Service';
import { formatMoneyBRL, toDateInputValue } from './utils';
import { FinanceWarning } from './FinanceWarning';

const service = new FinanceiroService();

type Mode = 'competence' | 'cash';

export default function FluxoCaixa() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<any>(null);
    const [accounts, setAccounts] = useState<any[]>([]);

    const defaultTo = useMemo(() => new Date(), []);
    const defaultFrom = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    }, []);

    const [mode, setMode] = useState<Mode>('cash');
    const [from, setFrom] = useState<string>(toDateInputValue(defaultFrom));
    const [to, setTo] = useState<string>(toDateInputValue(defaultTo));

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const [res, accountsRes] = await Promise.all([
                service.fluxoCaixa({ from, to, mode }),
                service.accountsFindAll(),
            ]);
            setData(res?.data || null);
            setAccounts(Array.isArray(accountsRes?.data) ? accountsRes.data : []);
        } catch (e: any) {
            setError(String(e?.response?.data?.message || 'Erro ao carregar fluxo de caixa'));
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totals = data?.totals || {};
    const withoutPayment = data?.warnings?.appointmentsWithoutPayment;
    const salesWithoutPayment = data?.warnings?.salesWithoutPayment;

    return (
        <div className="flex flex-col gap-4">
            <div className="card bg-base-100 shadow">
                <div className="card-body py-3">
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">Modo</span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full md:w-44"
                                value={mode}
                                onChange={(e) => setMode(e.target.value as any)}
                            >
                                <option value="cash">Caixa</option>
                                <option value="competence">Competência</option>
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">De</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">Até</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                            />
                        </div>
                        <button className="btn btn-sm btn-primary md:ml-auto" onClick={load}>
                            Atualizar
                        </button>
                    </div>
                    {error && (
                        <div className="alert alert-error mt-3">
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            </div>

            {mode === 'cash' && !loading && salesWithoutPayment?.count > 0 && (
                <FinanceWarning>
                    {salesWithoutPayment.count === 1 ? '1 venda paga' : `${salesWithoutPayment.count} vendas pagas`} sem forma de pagamento,
                    somando {formatMoneyBRL(salesWithoutPayment.amount)}. Esse valor não está nas entradas.
                </FinanceWarning>
            )}
            {mode === 'cash' && !loading && withoutPayment?.count > 0 && (
                <FinanceWarning>
                    {withoutPayment.count === 1 ? '1 atendimento concluído' : `${withoutPayment.count} atendimentos concluídos`} sem forma de
                    pagamento, somando {formatMoneyBRL(withoutPayment.amount)}. Esse valor não está nas entradas. Para incluir, abra o
                    atendimento na agenda e use Registrar pagamento.
                </FinanceWarning>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Entradas</div>
                        <div className="text-2xl font-semibold">
                            {loading ? '...' : formatMoneyBRL(totals.inflow || 0)}
                        </div>
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Saídas</div>
                        <div className="text-2xl font-semibold">
                            {loading ? '...' : formatMoneyBRL(totals.outflow || 0)}
                        </div>
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Saldo líquido</div>
                        <div className="text-2xl font-semibold">
                            {loading ? '...' : formatMoneyBRL(totals.net || 0)}
                        </div>
                    </div>
                </div>
            </div>

            {mode === 'cash' && !loading && Array.isArray(data?.byAccount) && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="font-medium mb-2">Por conta</div>
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Conta</th>
                                        <th className="text-right">Entradas</th>
                                        <th className="text-right">Saídas</th>
                                        <th className="text-right">Saldo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.byAccount.map((r: any) => (
                                        <tr key={String(r.accountId)}>
                                            <td>
                                                {accounts.find((account) => String(account._id) === String(r.accountId))?.name ||
                                                    String(r.accountId)}
                                            </td>
                                            <td className="text-right">{formatMoneyBRL(r.inflow || 0)}</td>
                                            <td className="text-right">{formatMoneyBRL(r.outflow || 0)}</td>
                                            <td className="text-right">{formatMoneyBRL(r.net || 0)}</td>
                                        </tr>
                                    ))}
                                    {!data.byAccount.length && (
                                        <tr>
                                            <td colSpan={4}>Nenhum registro</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

